import { useCallback, useEffect, useRef, useState } from 'react';
import type { Control, UseFormWatch } from 'react-hook-form';
import {
  HEIC_CONVERSION_ERROR_MESSAGE,
  HeicConversionError,
  MAX_DOCUMENT_SELECTION_SIZE_KB,
  getDocumentSelectionError,
} from '@brighte/document-utils';
import { CheckboxField, RadioGroupField } from '@brighte/form-components';
import { ACCEPTED_FILE_TYPES } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/css';
import {
  IdentityVerificationOverallOutcome,
  PropertyTitleOwnerOutput,
  TitleOwnerVerificationStatus,
  TitleOwnerVerificationMethod,
  useCreateTitleOwnerDocumentMutation,
  useDeleteTitleOwnerDocumentMutation,
  useUpdateTitleOwnerDocumentStatusMutation,
  useVerifyTitleSearchOwnerPensionerMutation,
} from '@graphql/index';
import { useResizeDocumentForUpload, useUploadFilesToS3 } from '@hooks';
import { Accordion, AccordionItem } from '@spark-web/accordion';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Dropzone } from '@spark-web/dropzone';
import { Field } from '@spark-web/field';
import { CheckCircleIcon, DocumentTextIcon, ExclamationIcon } from '@spark-web/icon';
import { RadioCard } from '@spark-web/radio';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink, TextLinkButton } from '@spark-web/text-link';
import noaSampleImage from '../../../assets/noa_sample.svg';
import taxReturnSampleImage from '../../../assets/tax_return_sample.svg';
import DocumentExampleCard from './DocumentExampleCard';
import NoaHelpModal from './NoaHelpModal';
import PensionerConcessionCardForm, {
  isCardExpiryTodayOrFuture,
  isValidConcessionCardCrn,
} from './PensionerConcessionCardForm';
import RetireeSuperannuationForm from './RetireeSuperannuationForm';
import TitleOwnerDocumentLink from './TitleOwnerDocumentLink';
import { INCOME_TYPES } from './incomeTypes';
import { useTitleOwnerDocumentVerification } from './useTitleOwnerDocumentVerification';
import type { DocumentChoice, OwnerVerificationStatus, VerifyIncomeFormValues } from './verifyIncomeStatus';

type NoaVerificationErrorCode =
  | 'UNKNOWN_DOCUMENT_TYPE'
  | 'DOCUMENT_TYPE_MISMATCH'
  | 'NOTICE_OF_ASSESSMENT_OUT_OF_DATE'
  | 'NOTICE_OF_ASSESSMENT_NO_YEAR'
  | 'DOCUMENT_UNREADABLE'
  | 'NAME_MISMATCH';

type VerificationErrorContent = { description?: string };

const NOA_VERIFICATION_ERROR_CONTENT: Record<NoaVerificationErrorCode, VerificationErrorContent> = {
  UNKNOWN_DOCUMENT_TYPE: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  DOCUMENT_TYPE_MISMATCH: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  NOTICE_OF_ASSESSMENT_OUT_OF_DATE: {
    description: 'Please upload the assessment for either FY25 or FY26.',
  },
  NOTICE_OF_ASSESSMENT_NO_YEAR: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and try again.',
  },
  DOCUMENT_UNREADABLE: {
    description:
      'The Notice of Assessment could not be verified. Please check that you uploaded the correct document and that any password protections are removed.',
  },
  NAME_MISMATCH: {
    description:
      "The name on this Notice of Assessment doesn't match the person listed on the property title. Please upload the NoA belonging to the person on title.",
  },
};

const SUPERANNUATION_VERIFICATION_ERROR_CONTENT: VerificationErrorContent = {
  description:
    "We couldn't verify this as a superannuation statement. Please check that you uploaded the correct document and try again.",
};

// The single body variant an owner card renders. Derived from income type and the owner's
// document choice — replaces several overlapping boolean flags.
type CardView = 'no_income' | 'concession_card' | 'super' | 'noa';

const VERIFICATION_SUCCESS_MESSAGES: Partial<Record<CardView, string>> = {
  noa: 'Notice of assessment is verified.',
  super: 'Superannuation statement is verified.',
};

type VerificationSuccessBannerProps = {
  cardView: CardView;
};

const VerificationSuccessBanner = ({ cardView }: VerificationSuccessBannerProps) => {
  const message = VERIFICATION_SUCCESS_MESSAGES[cardView];

  if (!message) return null;

  return (
    <Box display="flex" alignItems="center" gap="small" background="positiveLow" padding="medium" borderRadius="medium">
      <CheckCircleIcon tone="positive" size="xsmall" />
      <Text size="small">{message}</Text>
    </Box>
  );
};

const INCOME_ZERO_STATUS_CODE = 'INCOME_ZERO';

function formatMoney(amount: string): string {
  return `$${Number(amount).toLocaleString('en-AU')}`;
}

function getInitials(fullName: string): string {
  return fullName
    .split(' ')
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('');
}

type OwnerCardProps = {
  owner: PropertyTitleOwnerOutput;
  declaredIncome: string;
  incomeType?: string;
  trackingId: string;
  avatarBackground: 'primary' | 'secondary';
  isApplicant: boolean;
  control: Control<VerifyIncomeFormValues>;
  watch: UseFormWatch<VerifyIncomeFormValues>;
  onStatusChange: (ownerId: string, status: OwnerVerificationStatus) => void;
  // Reports the IDMatrix reference from a successful pensioner ACCEPT (null clears a stale one),
  // so the submit payload can send it and mark the owner verified.
  onIdMatrixReference: (ownerId: string, idMatrixReference: string | null) => void;
};

function OwnerVerificationCard({
  owner,
  declaredIncome,
  incomeType,
  trackingId,
  avatarBackground,
  isApplicant,
  control,
  watch,
  onStatusChange,
  onIdMatrixReference,
}: OwnerCardProps) {
  // Async upload/verify lifecycle — kept out of react-hook-form (transient, not committed form data).
  const [status, setStatus] = useState<OwnerVerificationStatus>('idle');
  const [verifyErrorCode, setVerifyErrorCode] = useState<NoaVerificationErrorCode | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // Documents already uploaded for this owner (from the title-search snapshot). Rendered below
  // the dropzone with a download link + delete X, mirroring the FileUpload document list. A
  // delete removes the row optimistically and restores it if deleteTitleOwnerDocument fails.
  const [uploadedDocuments, setUploadedDocuments] = useState<Array<{ id: string; filename: string }>>(
    (owner.documents ?? []).map(document => ({ id: document.id, filename: document.filename })),
  );
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [tooManyFilesError, setTooManyFilesError] = useState(false);
  // Filename of the NOA that legibly reported $0 income, shown alongside the pensioner prompt.
  const [zeroIncomeFileName, setZeroIncomeFileName] = useState<string | null>(null);
  const [isNoaHelpOpen, setIsNoaHelpOpen] = useState(false);
  // Bookkeeping that never affects rendered output — refs so mutating during the async loop
  // doesn't trigger re-renders. Files already create+uploaded+status'd (retry skips them).
  const uploadedDocumentsRef = useRef<Map<File, { id: string; filename: string }>>(new Map());
  // Counts every non-passing verify so all retry paths share one cap.
  const attemptCountRef = useRef(0);
  // The superannuation verifier passes a batch when at least one document passes. Keep the
  // passing document ids so removing an invalid file does not discard a still-valid verdict,
  // while removing the last passing file does.
  const passingSuperDocumentIdsRef = useRef<Set<string>>(new Set());
  const superVerificationInvalidatedByDeletionRef = useRef(false);
  const verificationAttemptGenerationRef = useRef(0);
  const removedDocumentNamesRef = useRef(new Map<string, string>());
  const removedSuperDocumentStateRef = useRef(
    new Map<string, { wasKnownPassing: boolean; invalidatedVerdict: boolean; verificationAttemptGeneration: number }>(),
  );
  // True while the owner sits in the manual-review fallback triggered purely by the cumulative
  // upload cap (as opposed to an attempt-cap failure or a real verify outcome). Lets us return
  // them to idle if they later remove a document and drop below the cap.
  const capManualReviewRef = useRef(false);
  // Signature of the concession-card details last confirmed via the "Verify Card" button, so
  // editing the details afterwards drops the owner back to un-provided until they verify again.
  const verifiedPensionerSignatureRef = useRef<string | null>(null);
  const [isVerifyingPensioner, setIsVerifyingPensioner] = useState(false);
  // Failed pensioner verify attempts (mutation rejections). Capped so a persistently failing
  // verify falls back to manual review rather than letting the owner retry forever.
  const [pensionerFailedAttempts, setPensionerFailedAttempts] = useState(0);

  // Only the "No income" income type shows the $0 self-declaration checkbox. Every other
  // income type follows its own verification branch, regardless of the declared amount.
  const isNoIncome = incomeType === INCOME_TYPES.NO_INCOME;

  const { mutateAsync: createDocument } = useCreateTitleOwnerDocumentMutation({ useErrorBoundary: false });
  const { mutateAsync: uploadToS3 } = useUploadFilesToS3();
  const resizeDocumentForUpload = useResizeDocumentForUpload();
  const { mutateAsync: updateDocumentStatus } = useUpdateTitleOwnerDocumentStatusMutation({ useErrorBoundary: false });
  const { mutateAsync: verifyDocuments } = useTitleOwnerDocumentVerification();
  const { mutateAsync: verifyPensioner } = useVerifyTitleSearchOwnerPensionerMutation({ useErrorBoundary: false });

  const updateStatus = useCallback(
    (next: OwnerVerificationStatus) => {
      setStatus(next);
      onStatusChange(owner.id, next);
    },
    [onStatusChange, owner.id],
  );

  // Documents allowed per upload/verify (2) and cumulatively across retries (6). At 6 the owner
  // must remove a document before they can upload or verify more.
  const MAX_FILES = 6;
  const MAX_FILES_PER_UPLOAD = 2;
  // Total verification attempts (any non-passing result) allowed before manual review.
  const MAX_ATTEMPTS = 3;
  // Failed "Verify Card" attempts allowed before falling back to manual review.
  const MAX_PENSIONER_VERIFY_ATTEMPTS = 3;

  // The Dropzone `accept` prop only filters the OS file picker — drag-and-drop (or "All files")
  // still delivers disallowed formats to onChange. Validate against ACCEPTED_FILE_TYPES here,
  // mirroring the FileUpload wrapper's isFileTypeAccepted so both upload paths agree.
  const isFileTypeAccepted = (file: File): boolean => {
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    return Object.entries(ACCEPTED_FILE_TYPES).some(([mimeType, extensions]) => {
      if (extensions.length > 0) {
        return extensions.includes(fileExtension);
      }
      if (mimeType.endsWith('/*')) {
        return file.type.startsWith(mimeType.replace('/*', '/'));
      }
      return file.type === mimeType;
    });
  };

  const handleFileDrop = (event: { target: { value: unknown } }) => {
    const value = event?.target?.value;
    const files = Array.isArray(value) ? (value as File[]) : [];
    setUploadError(undefined);
    // A fresh selection starts a new upload — forget any previously-uploaded docs.
    uploadedDocumentsRef.current = new Map();
    const invalidFiles = files.filter(file => !isFileTypeAccepted(file));
    if (invalidFiles.length > 0) {
      const extensions = Object.values(ACCEPTED_FILE_TYPES).flat().join(', ');
      setTooManyFilesError(false);
      setUploadError(`Unsupported file format. Please upload a file in one of the following formats: ${extensions}`);
      setSelectedFiles([]);
      return;
    }
    const selectionError = files.map(getDocumentSelectionError).find(Boolean);
    if (selectionError) {
      setTooManyFilesError(false);
      setUploadError(selectionError);
      setSelectedFiles([]);
      return;
    }
    if (files.length > MAX_FILES_PER_UPLOAD) {
      setTooManyFilesError(true);
      setSelectedFiles([]);
      return;
    }
    // Cumulative cap across retries: already-uploaded + newly-selected must not exceed MAX_FILES.
    // At the cap the owner must remove a document before uploading/verifying more.
    if (uploadedDocuments.length + files.length > MAX_FILES) {
      setTooManyFilesError(false);
      setUploadError(`You can upload up to ${MAX_FILES} documents in total. Please remove a document and try again.`);
      setSelectedFiles([]);
      return;
    }
    setTooManyFilesError(false);
    setSelectedFiles(files);
  };

  const handleUploadAndVerify = async (verificationMethod: TitleOwnerVerificationMethod) => {
    if (selectedFiles.length === 0) return;

    verificationAttemptGenerationRef.current += 1;
    const previousStatus = status;
    updateStatus('uploading');
    setUploadError(undefined);
    setVerifyErrorCode(null);

    try {
      // Carry forward files uploaded on a previous attempt so a retry resumes the rest.
      const currentUploaded = new Map(uploadedDocumentsRef.current);
      let failedCount = 0;
      let heicConversionFailed = false;

      for (const file of selectedFiles) {
        if (currentUploaded.has(file)) continue;

        try {
          // HEIC is transcoded to JPEG and large files are compressed by the backend; small files
          // pass through. Conversion renames .heic to .jpg, so read the name off the returned file.
          const uploadFile = await resizeDocumentForUpload(file);

          const createResult = await createDocument({
            input: {
              applicationId: trackingId,
              titleSearchOwnerId: owner.id,
              filename: uploadFile.name,
              fileSize: uploadFile.size,
            },
          });

          const { document, url, fieldsJson } = createResult.createTitleOwnerDocument;

          if (!url || !fieldsJson) {
            throw new Error('Failed to get presigned upload URL');
          }

          const uploadMetaData = JSON.parse(fieldsJson);
          const formData = new FormData();
          Object.keys(uploadMetaData).forEach(key => formData.append(key, uploadMetaData[key]));
          formData.append('file', uploadFile);

          await uploadToS3({ bucketUrl: url, file: formData });

          await updateDocumentStatus({
            input: {
              applicationId: trackingId,
              titleSearchOwnerId: owner.id,
              documentId: document.id,
            },
          });

          currentUploaded.set(file, { id: document.id, filename: uploadFile.name });
        } catch (error) {
          if (error instanceof HeicConversionError) {
            heicConversionFailed = true;
          }
          failedCount++;
        }
      }

      uploadedDocumentsRef.current = currentUploaded;

      if (failedCount > 0) {
        const succeeded = currentUploaded.size;
        const total = selectedFiles.length;

        if (succeeded > 0) {
          setUploadError('Few files failed to upload. Please try again.');
          datadogRum.addAction('LandOwner Document Partial Upload Error', { trackingId, succeeded, total });
        } else {
          // Prefer the conversion copy — it tells the customer what to do rather than just that it broke.
          setUploadError(
            heicConversionFailed
              ? HEIC_CONVERSION_ERROR_MESSAGE
              : 'Something went wrong uploading your document. Please try again.',
          );
          datadogRum.addAction('LandOwner Document Upload Error', { trackingId });
        }

        updateStatus(previousStatus);
        return;
      }

      // Record the freshly-uploaded docs so they count toward the cumulative cap and appear in the
      // removable list (deleting one frees a slot to upload/verify again).
      const newlyUploaded = selectedFiles.map(file => ({
        id: currentUploaded.get(file)?.id as string,
        filename: currentUploaded.get(file)?.filename ?? file.name,
      }));
      setUploadedDocuments(previous => {
        const existingIds = new Set(previous.map(document => document.id));
        return [...previous, ...newlyUploaded.filter(document => document.id && !existingIds.has(document.id))];
      });

      const documentIds = selectedFiles.map(file => currentUploaded.get(file)?.id as string);

      const verifyResult = await verifyDocuments({
        input: {
          applicationId: trackingId,
          titleSearchOwnerId: owner.id,
          documentIds,
          verificationMethod,
        },
      });

      const { validation, statusCode, documents } = verifyResult.verifyTitleOwnerDocuments;

      if (verificationMethod === TitleOwnerVerificationMethod.SUPERANNUATION) {
        passingSuperDocumentIdsRef.current = new Set(
          documents.filter(document => document.validation === 'Passed').map(document => document.documentId),
        );
        if (validation === 'Passed') {
          superVerificationInvalidatedByDeletionRef.current = false;
        }
      }

      if (validation === 'Passed') {
        updateStatus('passed');
        setVerifyErrorCode(null);
        datadogRum.addAction('LandOwner Document Verified', { trackingId });
      } else if (statusCode === INCOME_ZERO_STATUS_CODE) {
        // The NOA legibly reports $0 income — don't treat it as unreadable. The owner confirms
        // consent and continues (no retry); deliberately not counted toward the retry cap.
        setVerifyErrorCode(null);
        setZeroIncomeFileName(selectedFiles[0]?.name ?? null);
        updateStatus('income_zero_noa');
        datadogRum.addAction('LandOwner Document Zero Income', { trackingId });
      } else {
        // Unreadable / mismatched document — retryable, capped at MAX_ATTEMPTS.
        attemptCountRef.current += 1;
        const nextAttempt = attemptCountRef.current;

        const mapped = statusCode ? NOA_VERIFICATION_ERROR_CONTENT[statusCode as NoaVerificationErrorCode] : undefined;
        const resolvedCode = mapped ? (statusCode as NoaVerificationErrorCode) : 'DOCUMENT_UNREADABLE';
        setVerifyErrorCode(resolvedCode);

        if (nextAttempt >= MAX_ATTEMPTS) {
          updateStatus('failed_final');
          datadogRum.addAction('LandOwner Document Verify Final Failed', {
            trackingId,
            errorCode: resolvedCode,
          });
        } else {
          updateStatus(nextAttempt === 1 ? 'failed_once' : 'failed_twice');
        }
      }

      setSelectedFiles([]);
      uploadedDocumentsRef.current = new Map();
    } catch {
      setUploadError('Something went wrong uploading your document. Please try again.');
      datadogRum.addAction('LandOwner Document Upload Error', { trackingId });
      updateStatus(previousStatus);
    }
  };

  const isUploading = status === 'uploading';
  const hasFinallyFailed = status === 'failed_final';
  const hasPassed = status === 'passed';
  const isZeroIncomeNoa = status === 'income_zero_noa';
  // Once a $0-NOA owner is prompted, the upload step is done — don't re-open the dropzone.
  const showUploadSection = !isZeroIncomeNoa;
  const isUploadDisabled = hasPassed || hasFinallyFailed || isUploading || isZeroIncomeNoa;
  // Cumulative cap reached: block uploading and verifying until a document is removed.
  const isAtFileCap = uploadedDocuments.length >= MAX_FILES;
  const dropzoneDisabled = isUploadDisabled || isAtFileCap;
  const disabledDropzoneClassName = css({ pointerEvents: 'none', opacity: 0.5 });

  // Field values live in react-hook-form; read them here to drive the view + side effects.
  const documentChoice = watch(`documentChoice.${owner.id}`) ?? 'PRIMARY';
  const zeroIncomeConfirmed = !!watch(`zeroIncomeConfirmed.${owner.id}`);
  const concessionCard = watch(`concessionCard.${owner.id}`);

  const isPensioner = incomeType === INCOME_TYPES.PENSIONER;
  const isSelfFundedRetiree = incomeType === INCOME_TYPES.RETIREE;
  const offersDocumentChoice = isPensioner || isSelfFundedRetiree;

  // A single view drives the whole card body, replacing several overlapping booleans.
  const cardView: CardView = isNoIncome
    ? 'no_income'
    : offersDocumentChoice && documentChoice === 'PRIMARY'
      ? isPensioner
        ? 'concession_card'
        : 'super'
      : 'noa';
  const cardViewRef = useRef(cardView);
  cardViewRef.current = cardView;

  // Switching document type restarts the owner's flow — fully reset both branches' verification
  // state, not just the current selection. Leaving the NOA retry counter or the pensioner cap
  // counter behind would leak across choices and cause premature manual review. Clearing the
  // stored IDMatrix reference is defensive: a verified pensioner (the only state that holds a
  // reference) can no longer reach the NOA option (the radio is locked once verified), so this
  // guards the reference-clearing should that lock ever change.
  const prevDocumentChoiceRef = useRef(documentChoice);
  useEffect(() => {
    if (prevDocumentChoiceRef.current === documentChoice) return;
    prevDocumentChoiceRef.current = documentChoice;
    setSelectedFiles([]);
    setUploadError(undefined);
    setTooManyFilesError(false);
    setVerifyErrorCode(null);
    attemptCountRef.current = 0;
    uploadedDocumentsRef.current = new Map();
    setPensionerFailedAttempts(0);
    verifiedPensionerSignatureRef.current = null;
    onIdMatrixReference(owner.id, null);
    updateStatus('idle');
  }, [documentChoice, owner.id, onIdMatrixReference, updateStatus]);

  // "No income" owners are done once they tick the $0 self-declaration checkbox.
  useEffect(() => {
    if (!isNoIncome) return;
    updateStatus(zeroIncomeConfirmed ? 'zero_income_skip' : 'idle');
  }, [isNoIncome, zeroIncomeConfirmed, updateStatus]);

  // Concession Card: the owner fills the three fields then clicks "Verify Card" to persist
  // the details via verifyTitleSearchOwnerPensioner (save-and-continue) and mark them provided.
  const concessionName = concessionCard?.name;
  const concessionExpiry = concessionCard?.expiry;
  const concessionBirthdate = concessionCard?.birthdate;
  const concessionCrn = concessionCard?.crn;

  // Normalised card details, or null until every field is valid — the CRN must be a
  // well-formed Centrelink reference (9 digits + 1 letter), not merely non-empty.
  const pensionerCardDetails = (() => {
    const name = concessionName?.trim();
    const crn = concessionCrn?.trim();
    // The expiry must be a real, non-expired date (today or later) — an expired card can't verify.
    if (
      !name ||
      !concessionExpiry ||
      !concessionBirthdate ||
      !isCardExpiryTodayOrFuture(concessionExpiry) ||
      !isValidConcessionCardCrn(crn)
    )
      return null;
    // DatePickerField supplies a Date; normalise to a YYYY-MM-DD expiry.
    const expiryDate = concessionExpiry instanceof Date ? concessionExpiry : new Date(concessionExpiry as string);
    const dateOfExpiry = `${expiryDate.getFullYear()}-${String(expiryDate.getMonth() + 1).padStart(2, '0')}-${String(
      expiryDate.getDate(),
    ).padStart(2, '0')}`;
    // DatePickerField supplies a Date; normalise to a YYYY-MM-DD date of birth.
    const birthDate =
      concessionBirthdate instanceof Date ? concessionBirthdate : new Date(concessionBirthdate as string);
    const dateOfBirth = `${birthDate.getFullYear()}-${String(birthDate.getMonth() + 1).padStart(2, '0')}-${String(
      birthDate.getDate(),
    ).padStart(2, '0')}`;
    return {
      name,
      crn: crn as string,
      dateOfExpiry,
      dateOfBirth,
      signature: `${name}|${crn}|${dateOfExpiry}|${dateOfBirth}`,
    };
  })();
  const canVerifyPensioner = pensionerCardDetails !== null;
  const pensionerSignature = pensionerCardDetails?.signature ?? null;
  // Retry cap hit — stop further requests and fall back to manual review (non-blocking).
  const pensionerCapReached = pensionerFailedAttempts >= MAX_PENSIONER_VERIFY_ATTEMPTS;
  // A previous attempt failed but retries remain — prompt the owner to try again. Attempts are
  // sticky (never reset), so also require the owner to be un-provided: a later successful verify
  // marks them 'provided' and the retry prompt must clear even though the counter stays > 0.
  const showPensionerRetryError =
    pensionerFailedAttempts > 0 && !pensionerCapReached && !isVerifyingPensioner && status !== 'provided';
  // Lock the document choice once the owner reaches a terminal outcome on EITHER path, so they
  // can't switch documents (which resets the attempt counters) and retry past the caps or discard
  // a completed verification. Terminal covers: a verified NOA ('passed'); a $0-income NOA awaiting
  // consent ('income_zero_noa'); a pensioner card ACCEPT or card verify cap ('provided'); a
  // classifier-verified superannuation statement ('passed'); and a document rejected the maximum
  // number of times ('failed_final').
  const isDocumentChoiceLocked =
    offersDocumentChoice && (status === 'passed' || status === 'provided' || hasFinallyFailed || isZeroIncomeNoa);

  // Editing the card (or before any verify) leaves the owner un-provided until they verify
  // the current details — reset to idle whenever they differ from what was last confirmed.
  // Once the retry cap is hit the owner is provided for manual review (non-blocking), so
  // editing then must not drop them back to idle (the button is disabled — they can't re-verify).
  useEffect(() => {
    if (cardView !== 'concession_card' || pensionerCapReached) return;
    if (pensionerSignature === null || pensionerSignature !== verifiedPensionerSignatureRef.current) {
      updateStatus('idle');
    }
  }, [cardView, pensionerCapReached, pensionerSignature, updateStatus]);

  const handleVerifyPensioner = async () => {
    if (!pensionerCardDetails || pensionerCapReached) return;
    const { name, crn, dateOfExpiry, dateOfBirth, signature } = pensionerCardDetails;
    setIsVerifyingPensioner(true);

    // Verified only when IDMatrix returns an ACCEPT outcome; a rejected mutation or any
    // other outcome (REJECT/ERROR/TIMEOUT/null) counts as a failed attempt.
    let outcomeAccepted = false;
    let acceptedReference: string | null = null;
    try {
      const result = await verifyPensioner({
        input: {
          applicationId: trackingId,
          titleSearchOwnerId: owner.id,
          centrelink: { nameOnCard: name, customerReferenceNumber: crn, dateOfExpiry, dateOfBirth },
        },
      });
      const { overallOutcome, idMatrixReference } = result.verifyTitleSearchOwnerPensioner;
      // An ACCEPT is only usable if it carries an IDMatrix reference — submit sends that
      // reference to mark the owner verified, so a reference-less ACCEPT would show as
      // "verified" in the UI yet flip to manual review at submit. Treat it as not accepted
      // so it follows the retry/cap → manual-review path and the UI stays consistent.
      outcomeAccepted = overallOutcome === IdentityVerificationOverallOutcome.ACCEPT && !!idMatrixReference;
      if (outcomeAccepted) {
        acceptedReference = idMatrixReference ?? null;
      } else {
        datadogRum.addAction('LandOwner Pensioner Verify Not Accepted', { trackingId });
      }
    } catch {
      datadogRum.addAction('LandOwner Pensioner Verify Error', { trackingId });
    } finally {
      setIsVerifyingPensioner(false);
    }

    if (outcomeAccepted) {
      // Verified — the owner is provided and can continue. Send the IDMatrix reference up so
      // submit can mark the owner verified (null if the ACCEPT somehow carried no reference).
      verifiedPensionerSignatureRef.current = signature;
      onIdMatrixReference(owner.id, acceptedReference);
      updateStatus('provided');
      return;
    }

    // Failed attempt. Attempts persist across detail edits — the cap is per owner. Clear any
    // reference from an earlier ACCEPT so a stale one can't be submitted (e.g. accept → edit → fail).
    onIdMatrixReference(owner.id, null);
    const nextAttempts = pensionerFailedAttempts + 1;
    setPensionerFailedAttempts(nextAttempts);
    if (nextAttempts >= MAX_PENSIONER_VERIFY_ATTEMPTS) {
      // Cap hit — don't block the owner. Mark them provided for manual review and remember
      // the details so the reset-to-idle effect keeps the owner provided.
      verifiedPensionerSignatureRef.current = signature;
      updateStatus('provided');
      datadogRum.addAction('LandOwner Pensioner Verify Cap Reached', { trackingId });
    }
    // Below the cap, leave the owner un-provided so they can retry.
  };

  // A carried-over superannuation document completes the document step only when its persisted
  // classifier verdict is VERIFIED. A document with FAILED/PENDING status must never unlock the
  // declaration flow. Removing the last verified document returns the owner to idle.
  //
  // NOA and superannuation: reaching the cumulative upload cap (6) without a passing result falls
  // the owner back to manual review — the same non-blocking outcome as the attempt cap. Without
  // this, 6 documents carried over from a previous session leave the owner trapped: the dropzone is
  // disabled AND the outer "Continue" button stays disabled (an idle owner never reaches a done
  // status). The fallback surfaces the manual-review alert and, via onStatusChange, unlocks
  // Continue. Removing a document drops below the cap and — only when the fallback was cap-induced
  // — returns them to idle.
  useEffect(() => {
    if (isUploading) return;

    if (cardView === 'super') {
      const hasPersistedVerifiedSuper =
        owner.verificationMethod === TitleOwnerVerificationMethod.SUPERANNUATION &&
        owner.verificationStatus === TitleOwnerVerificationStatus.VERIFIED;
      if (
        uploadedDocuments.length > 0 &&
        hasPersistedVerifiedSuper &&
        !superVerificationInvalidatedByDeletionRef.current &&
        status === 'idle'
      ) {
        updateStatus('passed');
        return;
      }

      if (isAtFileCap) {
        if (status === 'passed' || status === 'failed_final') {
          return;
        }
        capManualReviewRef.current = true;
        updateStatus('failed_final');
        return;
      }

      if (capManualReviewRef.current) {
        capManualReviewRef.current = false;
        updateStatus('idle');
        return;
      }

      if (uploadedDocuments.length === 0 && status === 'passed') {
        updateStatus('idle');
      }
      return;
    }

    if (cardView === 'noa') {
      if (isAtFileCap) {
        // Never override a real completed/terminal outcome (a verified NOA, a $0 NOA awaiting
        // consent, or an attempt-cap failure).
        if (status === 'passed' || status === 'income_zero_noa' || status === 'failed_final') {
          return;
        }
        capManualReviewRef.current = true;
        updateStatus('failed_final');
      } else if (capManualReviewRef.current) {
        capManualReviewRef.current = false;
        updateStatus('idle');
      }
    }
  }, [
    isAtFileCap,
    cardView,
    isUploading,
    owner.verificationMethod,
    owner.verificationStatus,
    status,
    uploadedDocuments.length,
    updateStatus,
  ]);

  const handleRemoveUploadedDocument = (documentId: string) => {
    const removedDocument = uploadedDocuments.find(document => document.id === documentId);
    if (!removedDocument) return;

    removedDocumentNamesRef.current.set(documentId, removedDocument.filename);
    if (cardView === 'super' && status === 'passed') {
      const hadKnownPassingDocuments = passingSuperDocumentIdsRef.current.size > 0;
      const wasKnownPassing = passingSuperDocumentIdsRef.current.delete(documentId);
      const invalidatedVerdict = hadKnownPassingDocuments
        ? wasKnownPassing && passingSuperDocumentIdsRef.current.size === 0
        : true;

      removedSuperDocumentStateRef.current.set(documentId, {
        wasKnownPassing,
        invalidatedVerdict,
        verificationAttemptGeneration: verificationAttemptGenerationRef.current,
      });
      if (invalidatedVerdict) {
        superVerificationInvalidatedByDeletionRef.current = true;
        updateStatus('idle');
      }
    }
    setUploadedDocuments(previous => previous.filter(document => document.id !== documentId));
  };
  const handleRestoreUploadedDocument = (documentId: string, fileName: string) => {
    const removedState = removedSuperDocumentStateRef.current.get(documentId);
    if (removedState?.wasKnownPassing) {
      passingSuperDocumentIdsRef.current.add(documentId);
    }
    if (
      removedState?.invalidatedVerdict &&
      removedState.verificationAttemptGeneration === verificationAttemptGenerationRef.current
    ) {
      superVerificationInvalidatedByDeletionRef.current = false;
      if (cardViewRef.current === 'super') {
        updateStatus('passed');
      }
    }
    removedSuperDocumentStateRef.current.delete(documentId);
    removedDocumentNamesRef.current.delete(documentId);
    setUploadedDocuments(previous =>
      previous.some(document => document.id === documentId)
        ? previous
        : [...previous, { id: documentId, filename: fileName }],
    );
  };
  const handleRemoveUploadedDocumentSettled = (documentId: string) => {
    removedSuperDocumentStateRef.current.delete(documentId);
    removedDocumentNamesRef.current.delete(documentId);
  };
  // Keep the mutation on the owner card: optimistic removal unmounts the document row, but this
  // card remains mounted so a delayed failure can still restore the document and its verdict.
  const { mutate: deleteDocument } = useDeleteTitleOwnerDocumentMutation({
    onError: (_error, variables) => {
      const documentId = variables.input.documentId;
      const fileName = removedDocumentNamesRef.current.get(documentId);
      if (fileName) {
        handleRestoreUploadedDocument(documentId, fileName);
      }
    },
    onSettled: (_data, _error, variables) => {
      handleRemoveUploadedDocumentSettled(variables.input.documentId);
    },
  });
  const handleDeleteUploadedDocument = (documentId: string) => {
    handleRemoveUploadedDocument(documentId);
    deleteDocument({ input: { applicationId: trackingId, titleSearchOwnerId: owner.id, documentId } });
  };

  // Already-uploaded documents shown below the dropzone (NOA + Superannuation paths). Each row
  // links to a presigned download URL and can be deleted; empty renders nothing.
  const uploadedDocumentsList =
    uploadedDocuments.length > 0 ? (
      <Stack gap="small">
        {uploadedDocuments.map(document => (
          <TitleOwnerDocumentLink
            key={document.id}
            applicationId={trackingId}
            titleSearchOwnerId={owner.id}
            documentId={document.id}
            fileName={document.filename}
            onRemoveDocument={handleDeleteUploadedDocument}
          />
        ))}
      </Stack>
    ) : null;

  const consentLabel = isApplicant
    ? 'I consent to Brighte using this document to verify my income for NSW Government Loan eligibility purposes.'
    : `I confirm I am authorised to upload documents on behalf of ${owner.fullName}, and that ${
        owner.fullName.split(' ')[0]
      } consents to Brighte verifying their income for this application.`;

  return (
    <Stack gap="large">
      <Box background="surfaceLow" borderRadius="medium" padding="large" borderWidth="standard" border="standard">
        <Stack gap="large">
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            justifyContent={{ mobile: 'start', tablet: 'spaceBetween' }}
            // Desktop: bottom-align so "Declared" sits in line with the name (the lower row of the
            // avatar/name stack). Mobile (≤740px) keeps the stacked start-aligned layout unchanged.
            alignItems={{ mobile: 'start', tablet: 'end' }}
            gap={{ mobile: 'large', tablet: 'medium' }}
            // Extra separation between the name/declared header and the fields below:
            // 8px on mobile (≤740px), 16px on desktop.
            marginBottom={{ mobile: 'small', tablet: 'large' }}
          >
            {/* Avatar stacked above the name at every breakpoint, matching the earlier
                eligibility screens (declare-income). */}
            <Box display="flex" flexDirection="column" alignItems="start" gap="medium">
              <Box
                borderRadius="full"
                background={avatarBackground}
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ width: 40, height: 40 }}
              >
                <Text weight="semibold" size="small">
                  {getInitials(owner.fullName)}
                </Text>
              </Box>
              <Text weight="semibold" size="xlarge">
                {owner.fullName}
              </Text>
            </Box>
            <Text tone="muted" size="small">
              Declared: {formatMoney(declaredIncome)}
            </Text>
          </Box>

          {hasPassed && <VerificationSuccessBanner cardView={cardView} />}

          {cardView === 'no_income' ? (
            <Stack gap="medium">
              <CheckboxField name={`zeroIncomeConfirmed.${owner.id}`} control={control}>
                {isApplicant
                  ? 'I confirm I have $0 taxable income and do not have a Notice of Assessment.'
                  : `I confirm ${owner.fullName} has $0 taxable income and does not have a Notice of Assessment.`}
              </CheckboxField>
              {zeroIncomeConfirmed && (
                <Box
                  display="flex"
                  alignItems="start"
                  gap="small"
                  background="cautionLow"
                  padding="medium"
                  borderRadius="medium"
                >
                  <Box flexShrink={0} paddingTop="xxsmall">
                    <ExclamationIcon tone="caution" size="xsmall" />
                  </Box>
                  <Text size="small">
                    We understand you don&apos;t have a Notice of Assessment. You can continue through to the
                    application and our team will contact you to manually check your eligibility.
                  </Text>
                </Box>
              )}
            </Stack>
          ) : (
            <>
              {offersDocumentChoice && (
                <RadioGroupField<VerifyIncomeFormValues, DocumentChoice>
                  name={`documentChoice.${owner.id}`}
                  control={control}
                  label="Which of the following documents can you provide?"
                  aria-label="Which of the following documents can you provide?"
                  disabled={isDocumentChoiceLocked}
                >
                  <Stack gap="small">
                    <RadioCard value="PRIMARY">
                      {isPensioner ? 'Pensioner Concession Card' : 'Superannuation statement'}
                    </RadioCard>
                    <RadioCard value="NOA">Notice of Assessment</RadioCard>
                  </Stack>
                </RadioGroupField>
              )}

              {cardView === 'concession_card' && (
                <PensionerConcessionCardForm
                  control={control}
                  ownerId={owner.id}
                  onVerify={handleVerifyPensioner}
                  canVerify={canVerifyPensioner}
                  isVerifying={isVerifyingPensioner}
                  verifyError={showPensionerRetryError}
                  capReached={pensionerCapReached}
                  verified={status === 'provided' && !pensionerCapReached}
                />
              )}

              {cardView === 'super' && (
                <Stack gap="medium">
                  <RetireeSuperannuationForm
                    onFileDrop={handleFileDrop}
                    tooManyFilesError={tooManyFilesError}
                    maxFiles={MAX_FILES}
                    disabled={dropzoneDisabled}
                  />

                  <CheckboxField name={`selfFundedRetireeDeclarations.${owner.id}`} control={control}>
                    I confirm I am a self-funded retiree and this is my source of income
                  </CheckboxField>

                  {uploadedDocumentsList}

                  {verifyErrorCode && !hasPassed && (
                    <Alert tone="critical">
                      <Text size="small">{SUPERANNUATION_VERIFICATION_ERROR_CONTENT.description}</Text>
                    </Alert>
                  )}

                  {uploadError && <Alert tone="critical">{uploadError}</Alert>}

                  {hasFinallyFailed ? (
                    <Box
                      display="flex"
                      alignItems="start"
                      gap="small"
                      background="cautionLow"
                      padding="medium"
                      borderRadius="medium"
                    >
                      <Box flexShrink={0} paddingTop="xxsmall">
                        <ExclamationIcon tone="caution" size="xsmall" />
                      </Box>
                      <Text size="small">
                        We weren&apos;t able to verify your superannuation statement. You can continue through to the
                        application and our team will contact you to manually check your eligibility.
                      </Text>
                    </Box>
                  ) : (
                    selectedFiles.length > 0 &&
                    !hasPassed && (
                      <Box display="flex" justifyContent="center">
                        <Button
                          onClick={() => handleUploadAndVerify(TitleOwnerVerificationMethod.SUPERANNUATION)}
                          loading={isUploading}
                          disabled={isUploading || isAtFileCap}
                        >
                          Upload and verify
                        </Button>
                      </Box>
                    )
                  )}
                </Stack>
              )}

              {cardView === 'noa' && (
                <>
                  {/* A $0-income NOA is terminal: the upload UI is replaced by the read amount and
                      the consent checkbox. Rendered here (below the now-locked document radio) so
                      the radio keeps its usual position instead of dropping below the checkbox. */}
                  {isZeroIncomeNoa && (
                    <Stack gap="medium">
                      {zeroIncomeFileName && (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap="small"
                          padding="medium"
                          borderRadius="medium"
                          borderWidth="standard"
                          border="standard"
                        >
                          <DocumentTextIcon tone="muted" size="small" />
                          <Text size="small">{zeroIncomeFileName}</Text>
                        </Box>
                      )}

                      <Stack gap="medium" marginY="large">
                        <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                          <Text size="small" tone="muted">
                            Income read from document
                          </Text>
                          <Text size="small" weight="semibold">
                            $0
                          </Text>
                        </Box>
                        <Divider />
                      </Stack>

                      {/* Income type is captured up front, so we no longer ask "Are you a pensioner?"
                        on a $0 NOA. The owner just confirms consent and continues — no retry. */}
                      <CheckboxField name={`consents.${owner.id}`} control={control}>
                        {consentLabel}
                      </CheckboxField>
                    </Stack>
                  )}

                  <Stack gap="medium">
                    {showUploadSection && (
                      <Box
                        marginY="large"
                        aria-disabled={dropzoneDisabled}
                        className={dropzoneDisabled ? disabledDropzoneClassName : undefined}
                      >
                        <Field label="Upload a Notice of Assessment (FY25 or FY26)" disabled={dropzoneDisabled}>
                          <Dropzone
                            key={dropzoneDisabled ? 'disabled' : 'active'}
                            accept={ACCEPTED_FILE_TYPES}
                            onChange={dropzoneDisabled ? undefined : handleFileDrop}
                            maxFileSizeKb={MAX_DOCUMENT_SELECTION_SIZE_KB}
                            maxFiles={0}
                            multiple={true}
                          />
                          <Text size="xsmall" tone={isAtFileCap ? 'critical' : 'muted'}>
                            {isAtFileCap
                              ? `You've reached the maximum of ${MAX_FILES} documents. Remove a document to upload or verify more.`
                              : `You can upload up to ${MAX_FILES_PER_UPLOAD} documents at a time (${MAX_FILES} in total).`}
                          </Text>
                        </Field>
                      </Box>
                    )}

                    {showUploadSection && uploadedDocumentsList}

                    {/* Verification failure + cap alerts sit directly below the upload field, matching
                        the Superannuation and Pensioner Concession Card flows. */}
                    {verifyErrorCode &&
                      !isUploadDisabled &&
                      (() => {
                        const errorContent = NOA_VERIFICATION_ERROR_CONTENT[verifyErrorCode];
                        return (
                          <Alert tone="critical">
                            <Stack gap="xsmall">
                              {errorContent.description && <Text size="small">{errorContent.description}</Text>}
                            </Stack>
                          </Alert>
                        );
                      })()}

                    {uploadError && <Alert tone="critical">{uploadError}</Alert>}

                    {tooManyFilesError && (
                      <Alert tone="critical" heading="Too many files selected">
                        <Text size="small">
                          You can only upload up to {MAX_FILES_PER_UPLOAD} documents at a time. Please select fewer
                          files and try again.
                        </Text>
                      </Alert>
                    )}

                    {hasFinallyFailed && (
                      <Box
                        display="flex"
                        alignItems="start"
                        gap="small"
                        background="cautionLow"
                        padding="medium"
                        borderRadius="medium"
                      >
                        <Box flexShrink={0} paddingTop="xxsmall">
                          <ExclamationIcon tone="caution" size="xsmall" />
                        </Box>
                        <Stack gap="xsmall">
                          <Text size="small">
                            We weren&apos;t able to verify your Notice of Assessment. You can continue through to the
                            application and our team will contact you to manually check your eligibility.
                          </Text>
                        </Stack>
                      </Box>
                    )}

                    {selectedFiles.length > 0 && !hasPassed && !hasFinallyFailed && (
                      <Box display="flex" justifyContent="center">
                        <Button
                          onClick={() => handleUploadAndVerify(TitleOwnerVerificationMethod.NOA)}
                          loading={isUploading}
                          disabled={isUploading || isAtFileCap}
                        >
                          Upload and verify
                        </Button>
                      </Box>
                    )}

                    {showUploadSection && (
                      <>
                        <Box display="flex" flexDirection={{ mobile: 'column', tablet: 'row' }} gap="small">
                          <DocumentExampleCard
                            variant="upload"
                            title="Upload this"
                            image={noaSampleImage}
                            imageAlt="Example Notice of Assessment document"
                          >
                            Titled as{' '}
                            <Text as="span" weight="semibold" inline size="small">
                              &ldquo;Notice of Assessment&rdquo;
                            </Text>{' '}
                            - the ATO sends it after you lodge.
                          </DocumentExampleCard>
                          <DocumentExampleCard
                            variant="avoid"
                            title="Not this"
                            image={taxReturnSampleImage}
                            imageAlt="Example tax return document"
                          >
                            Titled as{' '}
                            <Text as="span" weight="semibold" inline size="small">
                              &ldquo;Tax return&rdquo;
                            </Text>{' '}
                            - this is what you lodge, not the assessment.
                          </DocumentExampleCard>
                        </Box>

                        <Text size="small">
                          <TextLinkButton onClick={() => setIsNoaHelpOpen(true)} tone="primaryActive" weight="semibold">
                            Need help finding your Notice of Assessment?
                          </TextLinkButton>
                        </Text>

                        <NoaHelpModal isOpen={isNoaHelpOpen} onToggle={() => setIsNoaHelpOpen(false)} />
                      </>
                    )}
                  </Stack>

                  {hasPassed && (
                    <CheckboxField name={`consents.${owner.id}`} control={control}>
                      {consentLabel}
                    </CheckboxField>
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </Box>

      <Box>
        <Accordion type="multiple">
          {cardView === 'concession_card' ? (
            <>
              <AccordionItem value="q0" label="Why do I need to provide my Pensioner Concession Card?" level="4">
                <Text>
                  Your Pensioner Concession is an official government document that we can use to confirm your
                  eligibility for the program.
                </Text>
              </AccordionItem>
              <AccordionItem value="q1" label="I don't have the documents needed right now" level="4">
                <Text>
                  No problem, your progress has been saved. You can return to this step once you have your documents
                  ready by clicking the link in your invite email.
                </Text>
              </AccordionItem>
            </>
          ) : cardView === 'super' ? (
            <AccordionItem value="q1" label="I don't have the documents needed right now" level="4">
              <Text>
                No problem, your progress has been saved. You can return to this step once you have your documents ready
                by clicking the link in your invite email.
              </Text>
            </AccordionItem>
          ) : (
            <>
              <AccordionItem value="q0" label="Why do I need to upload my Notice of Assessment?" level="4">
                <Text>
                  The NSW Government requires income verification for this program. Your Notice of Assessment is an
                  official ATO document that confirms your taxable income. We compare it against the figure you declared
                  in the previous step to verify your household qualifies.
                </Text>
              </AccordionItem>
              <AccordionItem value="q1" label="I don't have the documents needed right now" level="4">
                <Text>
                  No problem, your progress has been saved. You can return to this step once you have your documents
                  ready by clicking the link in your invite email.
                </Text>
              </AccordionItem>
              <AccordionItem value="q2" label="Where can I find my Notice of Assessment?" level="4">
                <Text>
                  You can download a copy of your Notice of Assessment in{' '}
                  <TextLink href="https://my.gov.au/" tone="primary" target="_blank" rel="noopener noreferrer">
                    MyGov
                  </TextLink>
                  .
                </Text>
              </AccordionItem>
            </>
          )}
        </Accordion>
      </Box>
    </Stack>
  );
}

export default OwnerVerificationCard;
